import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { Api } from "../../../../services/interceptor";
import type { ITeamsSquad } from "../../tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { PlayerCard } from "../../../../components/PlayerCard";

function dedupeByPlayerId(players: ITeamsSquad[]): ITeamsSquad[] {
  const seen = new Set<string>();
  const out: ITeamsSquad[] = [];
  for (const p of players) {
    const id = p.playerId?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(p);
  }
  return out;
}

interface TeamInfoMembersProps {
  captainPlayerId?: string | null;
  viceCaptainPlayerId?: string | null;
  wkPlayerId?: string | null;
}

const TeamInfo_Members: React.FC<TeamInfoMembersProps> = ({
  captainPlayerId,
  viceCaptainPlayerId,
  wkPlayerId,
}) => {
  const navigate = useNavigate();
  const teamSnapshotId = sessionStorage.getItem("teamSnapshotId");

  const openPlayerProfile = (playerId: string | undefined) => {
    const id = playerId?.trim();
    if (!id) return;
    navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(id)}`, {
      state: { tabToRestore: "members" as const },
    });
  };

  const { isLoading: playersLoading, data: rawPlayers = [] } = useQuery<ITeamsSquad[]>({
    queryKey: [QUERY_KEY.getAllPlayers, teamSnapshotId],
    queryFn: async () => {
      const url = `${QUERY_KEY.getAllPlayers}/${teamSnapshotId}`;
      const response = await Api.get(url);
      return (response?.data ?? []) as ITeamsSquad[];
    },
    enabled: !!teamSnapshotId?.trim(),
  });

  const players = useMemo(() => dedupeByPlayerId(rawPlayers), [rawPlayers]);

  const renderSection = (title: string, list: ITeamsSquad[]) => (
    <section className="ti-section" aria-label={title}>
      <h2 className="ti-section-title">{title}</h2>
      <div className="player-card-wrap" role="list">
        {list.map((player) => {
          const playerId = player.playerId?.trim();
          const decoratedPlayer = {
            ...player,
            captain: Boolean(captainPlayerId?.trim() && captainPlayerId?.trim() === playerId),
            viceCaptain: Boolean(
              viceCaptainPlayerId?.trim() && viceCaptainPlayerId?.trim() === playerId
            ),
            wicketKeeper: Boolean(wkPlayerId?.trim() && wkPlayerId?.trim() === playerId),
          };

          return (
            <div key={player.playerId} role="listitem">
              <PlayerCard
                player={decoratedPlayer}
                onClick={() => openPlayerProfile(player.playerId)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="team-info-wrap-section">
      {playersLoading ? (
        <p className="ti-empty">Loading players…</p>
      ) : players.length === 0 ? (
        <p className="ti-empty">No players on this team.</p>
      ) : (
        renderSection("Players", players)
      )}
    </div>
  );
};

export default TeamInfo_Members;
